import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useRicercaRapida } from '../api/ricerca-rapida.js';
import { t } from '../i18n/lingua.js';
import { usePuoAprire } from '../permessi.js';

interface Voce {
  chiave: string;
  titolo: string;
  sotto: string;
  vai: () => void;
}

/**
 * La ricerca della barra in alto: ordini, clienti e articoli in un posto
 * solo. Ctrl+K (⌘K sul Mac) ci porta il cursore da qualunque pagina.
 *
 * Cerca dopo una breve pausa e da due lettere in su, come gli elenchi; le
 * frecce scorrono i risultati, Invio apre quello scelto, Esc chiude. Ogni
 * gruppo ha in fondo «Vedi tutti», che apre l'elenco con la stessa ricerca.
 */
export function RicercaRapida() {
  const navigate = useNavigate();
  const puo = usePuoAprire();
  const campo = useRef<HTMLInputElement>(null);
  const contenitore = useRef<HTMLDivElement>(null);
  const [testo, setTesto] = useState('');
  const [cercato, setCercato] = useState('');
  const [aperto, setAperto] = useState(false);
  const [scelto, setScelto] = useState(0);

  const sezioni = {
    ordini: puo('sales-orders'),
    clienti: puo('customers'),
    articoli: puo('items'),
  };
  const { attiva, ordini, clienti, articoli } = useRicercaRapida(cercato, sezioni);

  useEffect(() => {
    const tasto = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        campo.current?.focus();
        campo.current?.select();
      }
    };
    document.addEventListener('keydown', tasto);
    return () => document.removeEventListener('keydown', tasto);
  }, []);

  // si cerca a una pausa nella scrittura, non a ogni tasto
  useEffect(() => {
    const attesa = setTimeout(() => setCercato(testo.trim()), 250);
    return () => clearTimeout(attesa);
  }, [testo]);

  useEffect(() => setScelto(0), [cercato]);

  useEffect(() => {
    if (!aperto) return;
    const chiudi = (e: MouseEvent) => {
      if (!contenitore.current?.contains(e.target as Node)) setAperto(false);
    };
    document.addEventListener('mousedown', chiudi);
    return () => document.removeEventListener('mousedown', chiudi);
  }, [aperto]);

  const fatto = () => {
    setAperto(false);
    setTesto('');
    setCercato('');
    campo.current?.blur();
  };

  const gruppi: { titolo: string; voci: Voce[]; totale: number; caricamento: boolean }[] = [];
  if (sezioni.ordini) {
    gruppi.push({
      titolo: t('Ordini'),
      caricamento: ordini.isFetching,
      totale: ordini.data?.total ?? 0,
      voci: (ordini.data?.rows ?? []).map((o) => ({
        chiave: `o${o.id}`,
        titolo: `${o.orderNumber} · ${t('prot.')} ${o.protocolNumber}`,
        sotto: `${o.customer} · ${o.state}`,
        vai: () => void navigate({ to: '/sales-orders/$id', params: { id: String(o.id) } }),
      })),
    });
  }
  if (sezioni.clienti) {
    gruppi.push({
      titolo: t('Clienti'),
      caricamento: clienti.isFetching,
      totale: clienti.data?.total ?? 0,
      voci: (clienti.data?.rows ?? []).map((c) => ({
        chiave: `c${c.id}`,
        titolo: c.name,
        sotto: [c.bcCodeSpa, c.bcCodeInc, c.city].filter(Boolean).join(' · '),
        vai: () => void navigate({ to: '/customers/$id', params: { id: String(c.id) } }),
      })),
    });
  }
  if (sezioni.articoli) {
    gruppi.push({
      titolo: t('Articoli'),
      caricamento: articoli.isFetching,
      totale: articoli.data?.total ?? 0,
      voci: (articoli.data?.rows ?? []).map((a) => ({
        chiave: `a${a.id}`,
        titolo: a.sku,
        sotto: a.description,
        vai: () => void navigate({ to: '/items/$id', params: { id: String(a.id) } }),
      })),
    });
  }

  const tutte = gruppi.flatMap((g) => g.voci);
  const caricamento = gruppi.some((g) => g.caricamento);
  const elenco: Record<string, string> = {
    [t('Ordini')]: '/sales-orders',
    [t('Clienti')]: '/customers',
    [t('Articoli')]: '/items',
  };

  const tasti = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      fatto();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAperto(true);
      setScelto((n) => Math.min(n + 1, Math.max(tutte.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setScelto((n) => Math.max(n - 1, 0));
    } else if (e.key === 'Enter' && tutte[scelto]) {
      e.preventDefault();
      tutte[scelto].vai();
      fatto();
    }
  };

  let indice = -1;
  return (
    <div
      className="omnisearch solo-largo"
      ref={contenitore}
      onClick={(e) => {
        // tutto il riquadro porta al campo, tranne i risultati: lì il clic apre
        if (!(e.target as HTMLElement).closest('.omnisearch-risultati')) campo.current?.focus();
      }}
    >
      <span aria-hidden>⌕</span>
      <input
        ref={campo}
        type="search"
        value={testo}
        placeholder={t('Cerca ordini, clienti, articoli')}
        aria-label={t('Cerca ordini, clienti, articoli')}
        aria-expanded={aperto && attiva}
        aria-controls="ricerca-rapida-risultati"
        role="combobox"
        autoComplete="off"
        onChange={(e) => {
          setTesto(e.target.value);
          setAperto(true);
        }}
        onFocus={() => setAperto(true)}
        onKeyDown={tasti}
      />
      <kbd>{t('Ctrl K')}</kbd>

      {aperto && attiva && (
        <div className="menu omnisearch-risultati" id="ricerca-rapida-risultati" role="listbox">
          {tutte.length === 0 && (
            <div className="menu-label">{caricamento ? t('Cerco…') : t('Nessun risultato')}</div>
          )}
          {gruppi
            .filter((g) => g.voci.length > 0)
            .map((g) => (
              <div key={g.titolo} role="group" aria-label={g.titolo}>
                <div className="menu-label">{g.titolo}</div>
                {g.voci.map((v) => {
                  indice += 1;
                  const mio = indice;
                  return (
                    <button
                      key={v.chiave}
                      type="button"
                      role="option"
                      aria-selected={mio === scelto}
                      className="menu-item omnisearch-voce"
                      onMouseEnter={() => setScelto(mio)}
                      onClick={() => {
                        v.vai();
                        fatto();
                      }}
                    >
                      <span className="strong">{v.titolo}</span>
                      {v.sotto && <span className="dim">{v.sotto}</span>}
                    </button>
                  );
                })}
                {g.totale > g.voci.length && (
                  <button
                    type="button"
                    className="menu-item dim"
                    onClick={() => {
                      void navigate({ to: elenco[g.titolo] as never, search: { q: cercato } as never });
                      fatto();
                    }}
                  >
                    {t('Vedi tutti ({n})', { n: g.totale })}
                  </button>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
